import {
  Component,
  DoCheck,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import icMoreVert from '@iconify/icons-ic/twotone-more-vert';
import icClose from '@iconify/icons-ic/twotone-close';
import icPrint from '@iconify/icons-ic/twotone-print';
import icDownload from '@iconify/icons-ic/twotone-download';
import icDelete from '@iconify/icons-ic/twotone-delete';
import icPerson from '@iconify/icons-ic/twotone-person';
import icPhone from '@iconify/icons-ic/twotone-phone';
import icEmail from '@iconify/icons-ic/twotone-email';
import icVisibility from '@iconify/icons-ic/twotone-visibility';
import icVisibilityOff from '@iconify/icons-ic/twotone-visibility-off';
import { AbstractControl, FormGroupDirective, ValidationErrors, ValidatorFn } from '@angular/forms';
import { InputTypeInterface } from '../../interfaces/input-type.interface';
import { CrudModeEnum } from '../../enum/crud-mode.enum';
import { ValidatorErrorMessageInterface } from '../../interfaces/validator-error-message.interface';
import { combineLatest, Observable, ReplaySubject, Subscription, tap } from 'rxjs';
import icArrowDropDown from '@iconify/icons-ic/twotone-arrow-drop-down';
import { debounceTime, distinctUntilChanged, filter, map, startWith, switchMap } from 'rxjs/operators';
import { requireMatchHelper } from '../../../helpers/requireMatch.helper';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter } from '@angular/material-moment-adapter';
import { MatLegacyFormFieldAppearance as MatFormFieldAppearance } from '@angular/material/legacy-form-field';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';
import { isArray } from 'chart.js/helpers';
import { MatLegacyCheckboxChange as MatCheckboxChange } from '@angular/material/legacy-checkbox';
import { MatLegacyAutocompleteTrigger as MatAutocompleteTrigger } from '@angular/material/legacy-autocomplete';
import { convertToUnit, TimeDelay, TimeUnit } from '../../../utils/time-utils';
import { StringHelper } from '../../../helpers/string.helper';

/**
 * @description
 * @separator could any char between properties
 * @every is a boolean to know if we should add separator between each properties
 */
export interface IDisplayPropertyDecorator {
  separator: string;
  every: boolean;
}

@Component({
  selector: 'base-form-input',
  templateUrl: 'form-input-base.component.html',
  styleUrls: ['form-input-base.component.scss'],
  providers: [
    { provide: MAT_DATE_LOCALE, useValue: 'fr' },
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
    },
    {
      provide: MAT_DATE_FORMATS,
      useValue: {
        parse: {
          dateInput: 'DD/MM/YYYY',
        },
        display: {
          dateInput: 'DD/MM/YYYY',
          monthYearLabel: 'DD/MM/YYYY',
          dateA11yLabel: 'LL',
          monthYearA11yLabel: '   DD/MM/YYYY',
        },
      },
    },
  ],
})
export class FormInputBaseComponent implements OnInit, OnChanges, OnDestroy, DoCheck {
  protected subscription = new Subscription();

  // Mode recherche serveur : flux des références successives de [items$]. L'affichage suit le
  // DERNIER items$ fourni par le parent sans jamais reconstruire filteredStates$ (pas de reset
  // du filtre en cours de frappe) ni lire une référence périmée (pas de résultats en retard).
  private readonly itemsSource$ = new ReplaySubject<Observable<any[]>>(1);
  private autocompleteEmitSubscription: Subscription | null = null;

  @Input() preSelectedElement: Array<any>;

  @Input() controlName: string;
  @Input() label: string;
  @Input() type: typeof InputTypeInterface = 'text';
  @Input() appearance: MatFormFieldAppearance = 'fill';
  @Input() icon: object | string;
  @Input() obscure = false;
  @Input() hint: string;
  @Input() minNumber = 0;
  @Input() maxNumber: number;
  @Input() saveButtonLabel = 'ENREGISTRER';
  @Input() updateButtonLabel = 'MODIFIER';
  @Input() customValidateButtonLabel = 'ENREGISTRER';
  @Input() invalidPatternMessage = 'Valeur invalide';
  @Input() customErrorMessage = 'Valeur invalide';
  @Input() customMultipleSelectedTemplate: TemplateRef<any> = null;
  @Input() selectedItems: Array<any> = [];
  @Input() separator = ' ';
  @Input() defaultTimeUnit = TimeUnit.HOUR;
  @Input() initializedData: any = null;

  @Input() inputTextWidthAuto = false;
  @Input() inputTextHeightAuto = false;

  @Input() dateStartAt: Date;
  @Input() dateEndAt: Date;
  @Input() minDate: Observable<Date> | Date;
  @Input() maxDate: Date;
  @Input() maxLength: number;
  @Input() minLength: number;

  @Input() loading = false;
  @Input() items: Array<any>;
  @Input() items$: Observable<any>;
  @Input() itemGroups: Array<{ label: string; items: any[] }> = []; // Support des groupes pour select
  @Input() displayProperty: string;
  @Input() isStringArray = false;
  @Input() displayPropertyObject: (value: any) => string;
  @Input() autocompletePanelWidthAuto: any = false;
  @Input() autocompleteSearchIgnoreAccents = true;
  /** Sélectionne l'option dont le libellé correspond exactement au texte saisi (type autocomplete) */
  @Input() autoSelectExactMatch = false;
  @Output() autocompleteChange = new EventEmitter();
  @Output() optionChange = new EventEmitter();
  @Output() onSelectItems = new EventEmitter();

  @Input() mode: CrudModeEnum;
  @Input() cancelLabel = 'ANNULER';
  @Input() showCancelButton = true;
  @Input() entityLabel: string;
  @Input() placeholder = '';

  @Input() disabled = false;
  @Input() readonly = false;

  @Output() onFilter = new EventEmitter();
  @Output() onCreate = new EventEmitter();
  @Output() onCustom = new EventEmitter();
  @Output() onUpdate = new EventEmitter();
  @Output() onDelete = new EventEmitter();
  @Output() onImport = new EventEmitter();
  @Output() onLostFocus = new EventEmitter();
  @Output() onScroll = new EventEmitter();

  @Output() onChangeCheckBox = new EventEmitter<boolean>();
  @Input() ngModel: any;
  @Input() isStaticData: boolean;
  /** search-multiple uniquement (opt-in) : sélection affichée en chips dans le champ. */
  @Input() chipsInField = false;

  @Input() disableAutoSelect = false;
  @Input() forceUpperCase = false;

  @ViewChild(MatAutocompleteTrigger) private autocompleteTrigger?: MatAutocompleteTrigger;

  CrudModeEnum = CrudModeEnum;

  filteredStates$: Observable<any>;
  /** Contrôle sur lequel filteredStates$ est branché — sert à détecter le remplacement du FormGroup. */
  private autocompleteBoundControl: AbstractControl | null = null;
  @Input() dateFilter;

  icMoreVert = icMoreVert;
  icArrowDropDown = icArrowDropDown;
  icClose = icClose;
  icPrint = icPrint;
  icDownload = icDownload;
  icDelete = icDelete;
  icPerson = icPerson;
  icPhone = icPhone;
  icEmail = icEmail;
  icVisibility = icVisibility;
  icVisibilityOff = icVisibilityOff;

  visible = false;
  isCleared = false;

  separatorKeysCodes: number[] = [ENTER, COMMA];
  defaultErrorMessage: ValidatorErrorMessageInterface;

  chipsValues: any[] = [];

  @Input() isFromContract = false;
  @Output() dateChanged = new EventEmitter<MatDatepickerInputEvent<Date>>();

  @Input() defaultValue: any;
  @Input() isInteger = false;
  @Input() typeSelection: 'number' | 'date' = 'number';
  @Input() selection: 'between' | 'fixed' | 'greaterThan' | 'lessThan' = 'between';
  @Input() withoutPadding: boolean;
  @Input() whiteBg: false;

  @Input() isNegativeNumber = false;

  constructor(public fcd: FormGroupDirective) {}

  ngOnInit() {
    this.setupDatePickerFilter();
    // this.setValidatorErrorMessage();
    if (this.forceUpperCase) {
      this.setToUpperCase();
    }

    if (this.defaultValue !== undefined) {
      this.fcd.form.get(this.controlName)?.setValue(this.defaultValue);
    }

    if (this.isInteger) {
      switch (this.type) {
        case 'timeDelay':
          this.fcd.form.get(this.controlName)?.setValidators([this.timeDelayIntegerValidator()]);
          break;
        default:
          this.fcd.form.get(this.controlName)?.setValidators([this.integerValidator()]);
      }
    }
  }

  toggleInput() {
    if (this.visible) {
      this.type = 'password';
      this.visible = false;
    } else {
      this.type = 'text';
      this.visible = true;
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.label) {
      this.setValidatorErrorMessage();
    }

    if (changes.disabled) {
      this.setDisabled();
    }

    if (changes.items || changes.items$) {
      if (changes.items$ && this.items$) {
        // Pousse la nouvelle référence vers le mode recherche serveur, sans reconstruire le pipeline
        this.itemsSource$.next(this.items$);
      }
      // Ne pas recréer filteredStates$ quand items$ change si déjà initialisé,
      // car startWith('') réinitialise le filtre local de l'autocomplete
      if (changes.items || !this.filteredStates$) {
        this.setAutoCompleteState();
      }
      this.setSelectState();
    }

    if (changes.defaultValue) {
      this.fcd.form.get(this.controlName)?.setValue(this.defaultValue);
    }

    if (changes.isInteger && this.isInteger) {
      switch (this.type) {
        case 'timeDelay':
          this.fcd.form.get(this.controlName)?.setValidators([this.timeDelayIntegerValidator()]);
          break;
        default:
          this.fcd.form.get(this.controlName)?.setValidators([this.integerValidator()]);
      }
    }
  }

  ngDoCheck() {
    // Si le FormGroup parent a été recréé (ex. écran validators d'établissement : initForm() à
    // chaque sélection de catégorie), filteredStates$ reste branché sur le valueChanges de
    // l'ANCIEN contrôle : la saisie n'émet plus ni le filtre local ni autocompleteChange.
    // On re-branche dès que l'instance du contrôle change (comparaison de référence, coût nul).
    if (this.filteredStates$ && this.autocompleteBoundControl) {
      const currentControl = this.fcd?.form?.controls?.[this.controlName];
      if (currentControl && currentControl !== this.autocompleteBoundControl) {
        this.setAutoCompleteState();
      }
    }
  }

  private setValidatorErrorMessage() {
    // Messages spécifiques pour les champs de type file
    if (this.type === 'file') {
      this.defaultErrorMessage = {
        required: 'Fichier obligatoire',
        incorrect: "Le fichier n'est pas valide",
      };
      return;
    }

    // Messages par défaut pour tous les autres types de champs
    this.defaultErrorMessage = {
      required: `Le champ ${this.label} est obligatoire`,
      incorrect: `Le champ ${this.label} n'est pas correct`,
      email: `Le champ ${this.label} n'est pas valide`,
      min: `${this.label} doit être supérieur ou égale à`,
      max: `${this.label} doit être inférieur ou égale à`,
      minLength: `${this.label} doit être supérieur ou égale à`,
      maxLength: `${this.label} doit être inférieur à`,
      isInteger: `${this.label} doit être un nombre entier`,
    };
  }

  private setAutoCompleteState() {
    if (this.type === 'autocomplete' || this.type === 'autocomplete-multiple' || this.type === 'search-multiple') {
      this.autocompleteBoundControl = this.fcd?.form?.controls?.[this.controlName] ?? null;
      if (this.items$) {
        const control = this.fcd?.form?.controls[this.controlName];
        control?.addValidators(requireMatchHelper);

        if (control && this.autocompleteChange.observed) {
          // Un parent écoute (autocompleteChange) : recherche serveur
          this.setServerSearchState(control);
        } else {
          // items$ statique : filtrage purement client
          this.filteredStates$ = control?.valueChanges.pipe(
            startWith(''),
            debounceTime(300),
            switchMap(state => {
              const result = this._filter(state);
              return result;
            }),
          );
        }
      }

      if (this.items) {
        this.fcd?.form?.controls[this.controlName].addValidators(this.requireMatchHelperItem());
        this.filteredStates$ = this.fcd.form.controls[this.controlName].valueChanges.pipe(
          startWith(''),
          debounceTime(500),
          map(state => this._filter(state)),
        );
      }
    }
  }

  private setSelectState() {
    if (this.type === 'select' && this.controlName) {
      this.setFirstItem(this.items);
    }
  }

  /**
   * Mode recherche serveur : un parent écoute (autocompleteChange) et réassigne [items$] en réponse.
   * Deux flux séparés :
   * - la saisie émet le terme de recherche au parent (debounce + distinct) ;
   * - l'affichage combine le DERNIER items$ reçu (itemsSource$, alimenté par ngOnChanges)
   *   avec le texte courant pour le filtre local.
   * filteredStates$ n'est jamais reconstruit quand items$ change : pas de reset du filtre en
   * cours de frappe, et les résultats affichés correspondent toujours à la dernière recherche.
   */
  private setServerSearchState(control: AbstractControl) {
    this.autocompleteEmitSubscription?.unsubscribe();
    this.autocompleteEmitSubscription = control.valueChanges
      .pipe(
        filter((value): value is string => typeof value === 'string'),
        debounceTime(300),
        distinctUntilChanged(),
      )
      .subscribe(value => {
        const search = this.autocompleteSearchIgnoreAccents ? this.removeAccentsValue(value) : value;
        this.autocompleteChange.emit(search);
      });
    this.subscription.add(this.autocompleteEmitSubscription);

    this.filteredStates$ = combineLatest([
      // distinctUntilChanged (par référence) : certains parents passent [items$] via un appel de
      // fonction retournant un asObservable() neuf à chaque change detection (ex. AutocompleteService) ;
      // sans dédoublonnage, chaque cycle réémettrait le même tableau et entretiendrait la CD en boucle
      this.itemsSource$.pipe(
        switchMap(items$ => items$),
        distinctUntilChanged(),
      ),
      control.valueChanges.pipe(startWith(control.value)),
    ]).pipe(
      map(([items, value]) => this.filterItemsLocally(items, value)),
      tap(items => this.setFirstItem(items)),
    );
  }

  _filter(value: any): Observable<any> | Array<any> {
    // if value is not string, return all items
    if (typeof value !== 'string') {
      // CORRECTION: Si items n'existe pas mais items$ existe, retourner items$ complet
      if (!this.items && this.items$) {
        return this.items$.pipe(
          tap(x => {
            this.setFirstItem(x);
          }),
        );
      }
      return this.items || [];
    }

    // this.isCleared = false;
    if (this.items$) {
      if (!value) {
        return this.items$.pipe(
          tap(x => {
            this.setFirstItem(x);
          }),
        );
      }

      return this.items$.pipe(
        map(x => this.filterItemsLocally(x, value)),
        tap(x => this.setFirstItem(x)),
      );
    }

    if (this.items) {
      this.setFirstItem(this.items);
      if (!value) {
        return this.items;
      }
      return this.filterItemsLocally(this.items, value);
    }

    return [];
  }

  private removeAccentsValue(value: string): string {
    if (typeof value === 'string') {
      return value?.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    }
    return value;
  }

  /**
   * Filtre local d'une liste d'options par le texte saisi (casse ignor\u00e9e, accents selon la config).
   * Source unique du filtrage client, partag\u00e9e par _filter (mode client) et le mode recherche serveur.
   */
  private filterItemsLocally(items: any[], value: any): any[] {
    if (typeof value !== 'string' || !value) {
      return items || [];
    }

    const searched = this.normalizeSearchText(value);
    return (items || []).filter(item => this.normalizeSearchText(this._displayFn(item)).includes(searched));
  }

  /** Normalisation commune texte saisi / libellé d'option : casse ignorée, accents selon la config. */
  private normalizeSearchText(text: string): string {
    return StringHelper.normalizeForSearch(text, this.autocompleteSearchIgnoreAccents);
  }

  _displayFn(data: any) {
    if (this.displayProperty) {
      return this.getProperties(data);
    }
    return data;
  }

  getProperties(data: any) {
    if (!data) {
      return '';
    }

    if (this.isStringArray) {
      if (isArray(data)) {
        return data[0];
      }
      return data;
    }

    const propertiesList = this.displayProperty.split(',');

    return propertiesList.reduce((acc, propertyPath, index) => {
      const properties = propertyPath.split('.');
      const value = this.getLastProperty(data, properties);
      return acc + value + (index < propertiesList.length - 1 ? this.separator : ' ');
    }, '');
  }

  getLastProperty(data: any, properties: string[]) {
    if (data && properties.length > 1) {
      let value = data;
      properties.forEach(property => {
        value = value[property.trim()];
      });
      if (Array.isArray(value) || typeof value === 'object') {
        return this.displayPropertyObject(data);
      }
      return value ?? '';
    } else if (data) {
      if (
        !!data[properties[0].trim()] &&
        (Array.isArray(data[properties[0].trim()]) || typeof data[properties[0].trim()] === 'object')
      ) {
        return this.displayPropertyObject(data);
      }
      return data[properties[0].trim()] ?? '';
    }
    return '';
  }

  resetAutoComplete() {
    this.isCleared = true;
    this.chipsValues = [];
    this.fcd.form.controls[this.controlName].reset();
    this.autocompleteChange.emit('');
    this.optionChange.emit(null);
  }

  setupDatePickerFilter() {
    if (this.dateStartAt && this.dateEndAt) {
      this.dateFilter = (d: Date | null): boolean => {
        return d >= this.dateStartAt && d <= this.dateEndAt;
      };
    } else if (this.dateStartAt) {
      this.dateFilter = (d: Date | null): boolean => {
        return d >= this.dateStartAt;
      };
    } else if (this.dateEndAt) {
      this.dateFilter = (d: Date | null): boolean => {
        return d <= this.dateEndAt;
      };
    }
  }

  private setDisabled() {
    if (this.disabled) {
      this.fcd?.form?.controls[this.controlName]?.disable();
    } else {
      this.fcd?.form?.controls[this.controlName]?.enable();
    }
  }

  private requireMatchHelperItem() {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.value && this.items?.length === 0) {
        return { incorrect: true };
      }

      if (
        control.value &&
        this.items?.length > 0 &&
        typeof control.value === 'string' &&
        !this.items?.includes(control.value)
      ) {
        return { incorrect: true };
      }
      return null;
    };
  }

  keydownAutocomplete($event: KeyboardEvent) {
    if (this.fcd.form.controls[this.controlName]?.value?.length === 1) {
      this.autocompleteChange.emit('');
    }
  }

  optionSelected($event) {
    if (this.type === 'autocomplete-multiple') {
      const selected = $event.option.value;
      const currentValue: any[] = this.fcd.form.get(this.controlName).value;
      // stop propagation

      if (this.isStringArray) {
        if (!this.chipsValues.some(item => item === selected)) {
          this.chipsValues = [...this.chipsValues, selected]; // Utilisez la déstructuration pour ajouter l'élément
          this.fcd.form.get(this.controlName).setValue(this.chipsValues); // Mettez à jour la valeur du FormControl
        }
      } else {
        if (!this.chipsValues.some(item => item.label === selected.label)) {
          this.chipsValues = [...this.chipsValues, selected]; // Utilisez la déstructuration pour ajouter l'élément
          this.fcd.form.get(this.controlName).setValue(this.chipsValues); // Mettez à jour la valeur du FormControl
        }
      }
    }

    this.optionChange.emit($event);
  }

  private setFirstItem(items: any[]) {
    if (
      items?.length === 1 &&
      !this.fcd.form?.controls[this.controlName]?.value &&
      !this.disableAutoSelect &&
      !this.isCleared
    ) {
      this.fcd.form?.controls[this.controlName].setValue(items[0]);
      this.fcd.form?.controls[this.controlName].updateValueAndValidity();
    }
    this.selectExactMatch(items);
  }

  /**
   * autoSelectExactMatch : si le texte saisi correspond exactement au libellé d'une seule option,
   * la sélectionne comme un clic sur la suggestion. Le panneau est fermé explicitement : un setValue
   * programmatique ne le ferme pas (il resterait ouvert, figé à son ancienne position).
   */
  private selectExactMatch(items: any[]) {
    const control = this.fcd?.form?.controls[this.controlName];
    if (!this.autoSelectExactMatch || this.type !== 'autocomplete' || typeof control?.value !== 'string') {
      return;
    }

    const typed = this.normalizeSearchText(control.value.trim());
    if (!typed) {
      return;
    }

    // trim : getProperties suffixe le libellé d'un espace
    const exactMatches = (items || []).filter(
      item => this.normalizeSearchText(this._displayFn(item)?.trim()) === typed,
    );
    if (exactMatches.length === 1) {
      control.setValue(exactMatches[0]);
      this.autocompleteTrigger?.closePanel();
    }
  }

  setToUpperCase(): void {
    this.fcd.form
      .get(this.controlName)
      .setValue(this.fcd.form.get(this.controlName).value?.toUpperCase(), { emitEvent: false });
    const sub = this.fcd.form.get(this.controlName).valueChanges.subscribe(value => {
      this.fcd.form.get(this.controlName).setValue(value?.toUpperCase(), { emitEvent: false });
    });
    this.subscription.add(sub);
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  clearChip(event) {
    if (this.isStringArray) {
      this.chipsValues = this.chipsValues.filter(item => item !== event);
    } else {
      this.chipsValues = this.chipsValues.filter(item => item.label !== event.label);
    }
    this.fcd.form.get(this.controlName).setValue(this.chipsValues);
  }

  signatureDateChanged(event: MatDatepickerInputEvent<Date>) {
    if (this.isFromContract) {
      this.dateChanged.emit(event);
    }
  }

  onCheckboxChange(event: MatCheckboxChange) {
    this.onChangeCheckBox.emit(event.checked);
  }

  private integerValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const isValid = Number.isInteger(Number(control.value));
      return isValid ? null : { isInteger: true };
    };
  }

  private timeDelayIntegerValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value as TimeDelay | null;
      if (!value || value.quantity == null) {
        return null;
      }
      const displayedQuantity = convertToUnit(value);
      if (!Number.isInteger(displayedQuantity)) {
        return { isInteger: true };
      }
      if (this.minNumber != null && displayedQuantity < this.minNumber) {
        return { min: true };
      }
      if (this.maxNumber != null && displayedQuantity > this.maxNumber) {
        return { max: true };
      }
      return null;
    };
  }
}
